import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import FilledButton from 'components/application/buttons/FilledButton';
import ModalBox from 'components/application/ModalBox';
import InfoPill from 'components/application/pills/InfoPill';

import { useCurrentActor } from 'components/contexts/CurrentActor';

export default function AllowanceExplanationModal({ onClose }) {
  const currentActor = useCurrentActor();
  const { pluralRAMSName, singularRAMSName } = currentActor.division.attributes;

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.AllowanceExplanationModal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const allowanceTypes = [
    {
      description: i18nT('types.fullUser.description'),
      title: i18nT('types.fullUser.title'),
    },
    {
      description: i18nT('types.personnelReporteeManager.description'),
      title: i18nT('types.personnelReporteeManager.title'),
    },
    {
      description: i18nT('types.ramsViewer.description', {
        interpolation: { escapeValue: false },
        pluralRAMSName,
      }),
      pill: i18nT('types.ramsViewer.pill'),
      title: i18nT('types.ramsViewer.title', {
        interpolation: { escapeValue: false },
        singularRAMSName: voca.capitalize(singularRAMSName),
      }),
    },
  ];

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <FilledButton color='mint' onClick={onClose}>
            {i18nTShared('close')}
          </FilledButton>
        </div>
      }
      isOpen={true}
      mode='letterbox'
      onClose={onClose}
    >
      <div className='modalbox-body'>
        <h2 className='tw-m-0 tw-mb-2 tw-text-xl tw-font-semibold tw-text-grey-900'>
          {i18nT('title')}
        </h2>
        <p className='tw-m-0 tw-text-m tw-font-normal tw-text-grey-700'>
          {i18nT('description')}{' '}
          <a
            className='tw-font-medium tw-text-blue-500 tw-underline'
            href='https://intercom.help/hands-hq/en/articles/5360816-user-permissions-and-division-access-explained'
            rel='noopener noreferrer'
            target='_blank'
          >
            {i18nT('readMore')}
          </a>
        </p>
        <ol className='tw-m-0 tw-mt-5 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0'>
          {allowanceTypes.map(({ description, pill, title }, index) => (
            <li
              className='tw-flex tw-items-center tw-gap-4 tw-rounded-2xl tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-grey-050 tw-px-4 tw-py-3'
              key={title}
            >
              <span className='tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-text-s tw-font-semibold tw-text-grey-700'>
                {index + 1}
              </span>
              <span className='tw-min-w-0 tw-grow'>
                <span className='tw-block tw-text-l tw-font-semibold tw-text-grey-900'>
                  {title}
                </span>
                <span className='tw-block tw-text-m tw-text-grey-500'>
                  {description}
                </span>
              </span>
              {pill && (
                <InfoPill
                  className='tw-shrink-0 tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-grey-050 tw-text-xs tw-tracking-wide tw-text-grey-700'
                  text={pill}
                />
              )}
            </li>
          ))}
        </ol>
      </div>
    </ModalBox>
  );
}

AllowanceExplanationModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};
